import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {
  Tip,
  WidgetInfo,
  WidgetSkeleton,
  fullNumber,
  fullPct,
  num,
  pct,
} from "./skdShared";

const FMM_COLORS = ["#1b1f4b", "#2f80ed", "#16adb8", "#f2994a", "#9b51e0"];
const FMM_OTHER = "#c4cad6";
const FMM_UNKNOWN = "#e0e3ea";

const formatMakeModelWord = (word) => {
  const w = String(word || "");
  if (w.length <= 3 || /\d/.test(w)) return w.toUpperCase();
  return w[0].toUpperCase() + w.slice(1).toLowerCase();
};

const niceMakeModelLabel = (value) =>
  String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map(formatMakeModelWord)
    .join(" ");

const modelMergeKey = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/[\s\-_.]/g, "");

const normalizeFleetMakeModel = (data = {}) => {
  const source =
    data && typeof data === "object" && !Array.isArray(data)
      ? data.data && typeof data.data === "object" && !Array.isArray(data.data)
        ? data.data
        : data
      : {};

  const makesMap = new Map();
  Object.entries(source).forEach(([raw, countRaw]) => {
    const count = num(countRaw);
    if (!count) return;
    const parts = String(raw || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return;
    const unknown = /^UNIDENTIFIED$/i.test(parts[0]);
    const makeName = unknown ? "Unidentified" : niceMakeModelLabel(parts[0]);
    const modelRaw = unknown ? "" : parts.slice(1).join(" ");
    if (!makesMap.has(makeName)) {
      makesMap.set(makeName, {
        name: makeName,
        count: 0,
        unknown,
        models: new Map(),
      });
    }
    const make = makesMap.get(makeName);
    make.count += count;
    const key = modelMergeKey(modelRaw) || "—";
    const existing = make.models.get(key);
    if (existing) {
      existing.count += count;
      if (count > existing.best) {
        existing.best = count;
        existing.name = modelRaw
          ? niceMakeModelLabel(modelRaw)
          : "Not specified";
      }
    } else {
      make.models.set(key, {
        name: modelRaw ? niceMakeModelLabel(modelRaw) : "Not specified",
        count,
        best: count,
      });
    }
  });

  const makes = [...makesMap.values()]
    .map((make) => ({
      ...make,
      models: [...make.models.values()].sort((a, b) => b.count - a.count),
    }))
    .sort(
      (a, b) => Number(a.unknown) - Number(b.unknown) || b.count - a.count,
    );

  let colorIndex = 0;
  makes.forEach((make) => {
    make.color = make.unknown
      ? FMM_UNKNOWN
      : colorIndex < FMM_COLORS.length
        ? FMM_COLORS[colorIndex++]
        : FMM_OTHER;
  });

  const total = makes.reduce((sum, make) => sum + make.count, 0);
  const modelCount = makes.reduce(
    (sum, make) => sum + (make.unknown ? 0 : make.models.length),
    0,
  );
  const knownMakes = makes.filter((make) => !make.unknown);
  const top = knownMakes.slice(0, 5);
  const rest = knownMakes.slice(5);
  const unidentified = makes.find((make) => make.unknown);
  const shareSegs = top.map((make) => ({
    name: make.name,
    count: make.count,
    color: make.color,
  }));
  if (rest.length) {
    shareSegs.push({
      name: `Others (${rest.length})`,
      count: rest.reduce((sum, make) => sum + make.count, 0),
      color: FMM_OTHER,
    });
  }
  if (unidentified) {
    shareSegs.push({
      name: "Unidentified",
      count: unidentified.count,
      color: unidentified.color,
    });
  }

  const allModels = makes
    .flatMap((make) =>
      make.models.map((model) => ({
        ...model,
        make: make.name,
        color: make.color,
        unknown: make.unknown,
        full: make.unknown ? "Unidentified" : `${make.name} ${model.name}`,
      })),
    )
    .sort(
      (a, b) => Number(a.unknown) - Number(b.unknown) || b.count - a.count,
    );

  return {
    makes,
    allModels,
    shareSegs,
    total,
    modelCount,
    makeCount: knownMakes.length,
    maxMake: Math.max(...makes.map((make) => make.count), 1),
    maxModel: Math.max(...allModels.map((model) => model.count), 1),
  };
};

const FleetMakeModelWidget = ({ loading, data, t }) => {
  const [makeModelView, setMakeModelView] = useState("make");
  const [makeModelSearch, setMakeModelSearch] = useState("");
  const [makeModelOpen, setMakeModelOpen] = useState(() => new Set());
  const makeModelOpenSeeded = useRef(false);

  const fleetMakeModelView = useMemo(
    () => normalizeFleetMakeModel(data),
    [data],
  );

  useEffect(() => {
    if (makeModelOpenSeeded.current) return;
    const top = fleetMakeModelView.makes.find((make) => !make.unknown);
    if (!top) return;
    makeModelOpenSeeded.current = true;
    setMakeModelOpen(new Set([top.name]));
  }, [fleetMakeModelView]);

  const filteredMakeModelRows = useMemo(() => {
    const q = makeModelSearch.trim().toLowerCase();
    if (makeModelView === "model") {
      const rows = fleetMakeModelView.allModels.filter(
        (model) => !q || model.full.toLowerCase().includes(q),
      );
      return { type: "model", rows };
    }
    const rows = fleetMakeModelView.makes
      .map((make) => {
        const makeMatch = !q || make.name.toLowerCase().includes(q);
        const models = makeMatch
          ? make.models
          : make.models.filter((model) =>
              model.name.toLowerCase().includes(q),
            );
        if (!makeMatch && !models.length) return null;
        return { ...make, models };
      })
      .filter(Boolean);
    return { type: "make", rows };
  }, [fleetMakeModelView, makeModelSearch, makeModelView]);

  const toggleMakeModelOpen = useCallback((name) => {
    setMakeModelOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const expandableMakeNames = useMemo(
    () =>
      fleetMakeModelView.makes
        .filter((make) => !make.unknown)
        .map((make) => make.name),
    [fleetMakeModelView],
  );

  const allMakesExpanded =
    expandableMakeNames.length > 0 &&
    expandableMakeNames.every((name) => makeModelOpen.has(name));

  const toggleAllMakeModels = useCallback(() => {
    setMakeModelOpen(() =>
      allMakesExpanded ? new Set() : new Set(expandableMakeNames),
    );
  }, [allMakesExpanded, expandableMakeNames]);

  if (loading) {
    return <WidgetSkeleton height={400} />;
  }

  return (
    <div className="skd-widget skd-widget--fleet-make-model">
      <header className="skd-fmm__head">
        <h3 className="skd-fmm__title">{t("skdFleetMakeModel")}</h3>
        <WidgetInfo title={t("skdFleetMakeModelInfo")} />
        <div className="skd-fmm__seg" role="group" aria-label={t("skdView")}>
          {[
            { type: "make", label: t("skdMake"), Icon: DirectionsCarIcon },
            {
              type: "model",
              label: t("skdModel"),
              Icon: FormatListBulletedIcon,
            },
          ].map(({ type, label, Icon }) => {
            const isActive = makeModelView === type;
            return (
              <button
                key={type}
                type="button"
                aria-pressed={isActive}
                className={isActive ? "is-active" : undefined}
                onClick={() => setMakeModelView(type)}
              >
                <Icon fontSize="small" />
                {label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="skd-fmm__sum">
        <span className="skd-fmm__big">
          {fleetMakeModelView.total.toLocaleString()}
          <small>{t("skdVehicles").toLowerCase()}</small>
        </span>
        <span className="skd-fmm__meta">
          <b>{fleetMakeModelView.makeCount}</b> {t("skdMakes")} ·{" "}
          <b>{fleetMakeModelView.modelCount}</b> {t("skdModels")}
        </span>
      </div>

      <div className="skd-fmm__share" aria-hidden="true">
        {fleetMakeModelView.shareSegs.map((seg) => {
          const sharePct = fleetMakeModelView.total
            ? (seg.count / fleetMakeModelView.total) * 100
            : 0;
          return (
            <Tip
              key={seg.name}
              title={`${seg.name}: ${fullNumber(seg.count)} (${fullPct(sharePct)})`}
              component="i"
              style={{
                width: `${sharePct}%`,
                background: seg.color,
              }}
            />
          );
        })}
      </div>
      <div className="skd-fmm__legend">
        {fleetMakeModelView.shareSegs.map((seg) => {
          const sharePct = fleetMakeModelView.total
            ? (seg.count / fleetMakeModelView.total) * 100
            : 0;
          return (
            <span key={seg.name}>
              <i style={{ background: seg.color }} />
              {seg.name} <b>{pct(sharePct, 1)}</b>
            </span>
          );
        })}
      </div>

      <label className="skd-fmm__search">
        <SearchIcon className="skd-fmm__search-icon" fontSize="small" />
        <input
          type="search"
          placeholder={t("skdSearchMakeModel")}
          aria-label={t("skdSearchMakeModel")}
          value={makeModelSearch}
          onChange={(e) => setMakeModelSearch(e.target.value)}
        />
      </label>

      <ul className="skd-fmm__list skd-scroll-area">
        {filteredMakeModelRows.type === "make"
          ? filteredMakeModelRows.rows.map((make) => {
              const forceOpen =
                Boolean(makeModelSearch.trim()) &&
                !make.name
                  .toLowerCase()
                  .includes(makeModelSearch.trim().toLowerCase());
              const open = makeModelOpen.has(make.name) || forceOpen;
              return (
                <li key={make.name}>
                  <button
                    type="button"
                    className="skd-fmm__row"
                    aria-expanded={open}
                    onClick={() => toggleMakeModelOpen(make.name)}
                  >
                    <KeyboardArrowRightIcon className="skd-fmm__chev" />
                    <span className="skd-fmm__nm">
                      {make.name}
                      {!make.unknown && (
                        <small>
                          {make.models.length}{" "}
                          {make.models.length === 1
                            ? t("skdModel").toLowerCase()
                            : t("skdModels").toLowerCase()}
                        </small>
                      )}
                    </span>
                    <span className="skd-fmm__val">
                      {make.count.toLocaleString()}
                      <small>
                        {pct(
                          fleetMakeModelView.total
                            ? (make.count / fleetMakeModelView.total) * 100
                            : 0,
                          1,
                        )}
                      </small>
                    </span>
                    <span className="skd-fmm__bar">
                      <i
                        style={{
                          width: `${(make.count / fleetMakeModelView.maxMake) * 100}%`,
                          background: make.color,
                        }}
                      />
                    </span>
                  </button>
                  {open && !make.unknown && (
                    <ul className="skd-fmm__sub">
                      {make.models.map((model) => (
                        <li key={`${make.name}-${model.name}`}>
                          <span>{model.name}</span>
                          <span className="skd-fmm__val">
                            {model.count.toLocaleString()}
                          </span>
                          <span className="skd-fmm__bar">
                            <i
                              style={{
                                width: `${(model.count / make.count) * 100}%`,
                                background: make.color,
                              }}
                            />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })
          : filteredMakeModelRows.rows.map((model) => (
              <li key={model.full}>
                <div className="skd-fmm__row skd-fmm__row--flat">
                  <span className="skd-fmm__nm">
                    {model.unknown ? t("skdUnidentified") : model.name}
                    {!model.unknown && <small>{model.make}</small>}
                  </span>
                  <span className="skd-fmm__val">
                    {model.count.toLocaleString()}
                    <small>
                      {pct(
                        fleetMakeModelView.total
                          ? (model.count / fleetMakeModelView.total) * 100
                          : 0,
                        1,
                      )}
                    </small>
                  </span>
                  <span className="skd-fmm__bar">
                    <i
                      style={{
                        width: `${(model.count / fleetMakeModelView.maxModel) * 100}%`,
                        background: model.color,
                      }}
                    />
                  </span>
                </div>
              </li>
            ))}
        {!filteredMakeModelRows.rows.length && (
          <li className="skd-fmm__empty">
            {makeModelSearch.trim()
              ? t("skdNoMakeModelMatch").replace(
                  "{{q}}",
                  makeModelSearch.trim(),
                )
              : t("skdNoData")}
          </li>
        )}
      </ul>

      <div className="skd-fmm__foot">
        <span>
          {makeModelView === "make"
            ? t("skdMakesCount")
                .replace("{{shown}}", String(filteredMakeModelRows.rows.length))
                .replace("{{total}}", String(fleetMakeModelView.makes.length))
            : t("skdModelsCount")
                .replace("{{shown}}", String(filteredMakeModelRows.rows.length))
                .replace(
                  "{{total}}",
                  String(fleetMakeModelView.allModels.length),
                )}
        </span>
        {makeModelView === "make" && (
          <button type="button" onClick={toggleAllMakeModels}>
            {allMakesExpanded ? t("skdCollapseAll") : t("skdExpandAll")}
          </button>
        )}
      </div>
    </div>
  );
};

export default FleetMakeModelWidget;
